/* ============================================================================
   domain_selector — type-driven operator catalogue + value modes.
   The terminal field's type drives which operators are offered and which value
   editor renders. `in`/`not in` use chips for enum · m2o · m2m · o2m; date &
   datetime add `between`. Pseudo-ops `set`/`unset`/`between` serialize specially
   (see serialize.ts).
   ========================================================================== */

export type ValueMode = "text" | "num" | "one" | "many" | "date" | "range" | "bool" | "none";

export interface OperatorDef {
    op: string;
    label: string;
    vmode: ValueMode;
}

const DATE_OPS: OperatorDef[] = [
    { op: "=", label: "on", vmode: "date" },
    { op: ">", label: "after", vmode: "date" },
    { op: "<", label: "before", vmode: "date" },
    { op: "between", label: "between", vmode: "range" },
    { op: "set", label: "is set", vmode: "none" },
    { op: "unset", label: "is not set", vmode: "none" },
];

const REL_OPS: OperatorDef[] = [
    { op: "=", label: "is", vmode: "one" },
    { op: "!=", label: "is not", vmode: "one" },
    { op: "in", label: "is any of", vmode: "many" },
    { op: "not in", label: "is none of", vmode: "many" },
    { op: "set", label: "is set", vmode: "none" },
    { op: "unset", label: "is not set", vmode: "none" },
];

export function opsForType(type: string): OperatorDef[] {
    switch (type) {
        case "char":
        case "text":
            return [
                { op: "ilike", label: "contains", vmode: "text" },
                { op: "=", label: "is", vmode: "text" },
                { op: "!=", label: "is not", vmode: "text" },
                { op: "set", label: "is set", vmode: "none" },
                { op: "unset", label: "is not set", vmode: "none" },
            ];
        case "number":
        case "integer":
        case "decimal":
        case "monetary":
            return [
                { op: "=", label: "equals", vmode: "num" },
                { op: "!=", label: "not equals", vmode: "num" },
                { op: ">", label: "greater than", vmode: "num" },
                { op: ">=", label: "at least", vmode: "num" },
                { op: "<", label: "less than", vmode: "num" },
                { op: "<=", label: "at most", vmode: "num" },
                { op: "set", label: "is set", vmode: "none" },
                { op: "unset", label: "is not set", vmode: "none" },
            ];
        case "enum":
        case "selection":
            return [
                { op: "=", label: "is", vmode: "one" },
                { op: "!=", label: "is not", vmode: "one" },
                { op: "in", label: "is any of", vmode: "many" },
                { op: "not in", label: "is none of", vmode: "many" },
            ];
        case "m2o":
        case "reference":
            return REL_OPS;
        case "m2m":
        case "o2m":
        case "many2many":
        case "one2many":
            return [
                { op: "in", label: "contains any of", vmode: "many" },
                { op: "not in", label: "contains none of", vmode: "many" },
                { op: "set", label: "is set", vmode: "none" },
                { op: "unset", label: "is empty", vmode: "none" },
            ];
        case "date":
        case "datetime":
            return DATE_OPS;
        case "bool":
        case "boolean":
            return [{ op: "=", label: "is", vmode: "bool" }];
        default:
            return [
                { op: "=", label: "is", vmode: "text" },
                { op: "!=", label: "is not", vmode: "text" },
            ];
    }
}

export function opMeta(type: string, op: string): OperatorDef {
    const ops = opsForType(type);
    return ops.find((o) => o.op === op) ?? ops[0];
}

export const RELATIONAL_TYPES = new Set(["m2o", "m2m", "o2m", "reference", "many2many", "one2many"]);
